import {
  SafetyFacilityType,
  SafetyIntxType,
} from "../../../calculations/safety/SPFConstants";
import { getSpfsForFacilityType } from "../../../calculations/safety/allSpfs";
import { getDistrictForCounty } from "../../../constants/geographyConstants";
import {
  ANALYSIS_TYPES,
  INTX_TYPES,
  NO_CURRENT_INTERSECTION,
  getSPFBaseIntersections,
} from "../../../constants/intxConstants";
import {
  INTERSECTION_NUMBERS,
  STEP_STORAGE_KEYS,
} from "../../../constants/storageConstants";
import { getAnalysisType } from "../../../util/dataSelectors";
import { SAFETY_INPUT_TYPES, SPF_FIELD_NAMES } from "../safety/safetyConstants";
import {
  getCmfOverridesForIntersection,
  getCmfsForIntxType,
  getGlobalAreaType,
  getSafetyAnalysisType,
} from "../safety/safetyHelpers";

const getSafetyIntxType = ({ intxType, is3LegIntersection }) => {
  switch (intxType) {
    case INTX_TYPES.TWSC:
      return is3LegIntersection
        ? SafetyIntxType.THREE_LEG_MINOR_STOP
        : SafetyIntxType.FOUR_LEG_MINOR_STOP;
    case INTX_TYPES.ALL_WAY_STOP:
      return is3LegIntersection
        ? SafetyIntxType.THREE_LEG_ALL_STOP
        : SafetyIntxType.FOUR_LEG_ALL_STOP;
    case INTX_TYPES.SIGNAL:
      return is3LegIntersection
        ? SafetyIntxType.THREE_LEG_SIGNAL
        : SafetyIntxType.FOUR_LEG_SIGNAL;
    case INTX_TYPES.ONE_LANE_ROUNDABOUT:
      return is3LegIntersection
        ? SafetyIntxType.THREE_LEG_ROUNDABOUT
        : SafetyIntxType.FOUR_LEG_ROUNDABOUT;
    default:
      throw new Error(
        `Could not map intersection type ${intxType} to a safety-specific intersection type`
      );
  }
};

/**
 * @param {string} currentIntersection the current intersection type at the project site
 * @param {string} intxType intersection type whose SPF is being used
 * @returns boolean
 */
const getIsExpectedCrashCalcPossible = ({ currentIntersection, intxType }) => {
  switch (currentIntersection) {
    case NO_CURRENT_INTERSECTION:
      return false;
    case INTX_TYPES.TWSC:
      return true;
    case INTX_TYPES.ALL_WAY_STOP:
      return intxType === INTX_TYPES.ALL_WAY_STOP;
    case INTX_TYPES.SIGNAL:
      return (
        intxType !== INTX_TYPES.TWSC && intxType !== INTX_TYPES.ALL_WAY_STOP
      );
    case INTX_TYPES.DIAMOND_WITH_SIGNALS:
      return intxType !== INTX_TYPES.DIAMOND_WITH_STOP_CONTROL;
    case INTX_TYPES.DIAMOND_WITH_STOP_CONTROL:
      return (
        intxType !== INTX_TYPES.DIAMOND_WITH_SIGNALS &&
        intxType !== INTX_TYPES.DDI &&
        intxType !== INTX_TYPES.SINGLE_POINT_DIAMOND
      );
    default:
      throw new Error(
        `could not find expected crash calculation info for currentIntersection ${currentIntersection}`
      );
  }
};

/**
 * @param {object} cmf contains Crash Modification Factors to be applied to the final crashesPerYear value
 * @param {object} projectData
 * @param {object} spfIntersection intersection details for the intersection we want to run the SPF for
 * @returns object
 */
const getCrashesPerYearForSpfIntersection = ({
  cmf = { fatalInjury: 1, total: 1 },
  projectData,
  spfIntersection,
}) => {
  const { county } = projectData[STEP_STORAGE_KEYS.PROJECT_INFORMATION];
  const { currentIntersection, numberOfLegs } =
    projectData[STEP_STORAGE_KEYS.GLOBAL_INPUT_DATA];
  const is3LegIntersection = numberOfLegs === "3";
  const isRampTerminalIntersection =
    getAnalysisType(projectData) === ANALYSIS_TYPES.RAMP_TERMINAL_INTERSECTION;
  const {
    areaType,
    intersection1: globalSafetyIntx1,
    intersection2: globalSafetyIntx2,
    majorRoad,
    minorRoad,
    observedFatalInjuryCrashes,
    observedTotalCrashes,
    safetyFacilityType,
    yearsOfObservedCrashData,
  } = projectData[STEP_STORAGE_KEYS.GLOBAL_SAFETY_DATA];
  const {
    getFatalInjuryPredicted,
    getTotalPredicted,
    getFatalInjuryExpected,
    getTotalExpected,
  } = getSpfsForFacilityType(
    isRampTerminalIntersection
      ? SafetyFacilityType.RAMP_TERMINAL
      : safetyFacilityType
  );

  const isExpectedCrashCalcPossible = getIsExpectedCrashCalcPossible({
    currentIntersection,
    intxType: spfIntersection.type,
  });
  const fatalInjurySpf = isExpectedCrashCalcPossible
    ? getFatalInjuryExpected
    : getFatalInjuryPredicted;
  const totalSpf = isExpectedCrashCalcPossible
    ? getTotalExpected
    : getTotalPredicted;

  if (isRampTerminalIntersection) {
    const getRampTerminalAadts = ({ isOpeningYear, isIntx1 }) => {
      const globalSafetyIntx = isIntx1 ? globalSafetyIntx1 : globalSafetyIntx2;
      const yearKey = isOpeningYear ? "openingYearAADT" : "designYearAADT";
      return {
        aadtin: globalSafetyIntx.insideCrossroadLeg[yearKey],
        aadtout: globalSafetyIntx.outsideCrossroadLeg[yearKey],
        aadten: globalSafetyIntx.entranceRamp[yearKey],
        aadtex: globalSafetyIntx.exitRamp[yearKey],
      };
    };
    const getSpfProps = ({ isOpeningYear, isIntx1 }) => {
      const globalSafety = isIntx1 ? globalSafetyIntx1 : globalSafetyIntx2;
      const COMMON_PROPS = {
        areaType,
        observedFatalInjuryCrashes: globalSafety.observedFatalInjuryCrashes,
        observedTotalCrashes: globalSafety.observedTotalCrashes,
        yearsOfAnalysis: globalSafety.yearsOfObservedCrashData,
        intxType: spfIntersection.type,
      };
      return {
        ...COMMON_PROPS,
        ...spfIntersection.safety[
          isIntx1
            ? INTERSECTION_NUMBERS.INTERSECTION_1
            : INTERSECTION_NUMBERS.INTERSECTION_2
        ],
        ...getRampTerminalAadts({ isOpeningYear, isIntx1 }),
      };
    };
    const getFiCrashes = ({ isOpeningYear, isIntx1 }) => {
      return (
        cmf.fatalInjury *
        fatalInjurySpf(getSpfProps({ isOpeningYear, isIntx1 }))
      );
    };
    const getTotalCrashes = ({ isOpeningYear, isIntx1 }) => {
      return cmf.total * totalSpf(getSpfProps({ isOpeningYear, isIntx1 }));
    };
    return {
      openingYear: {
        fatalInjury:
          getFiCrashes({ isOpeningYear: true, isIntx1: true }) +
          getFiCrashes({ isOpeningYear: true, isIntx1: false }),
        total:
          getTotalCrashes({ isOpeningYear: true, isIntx1: true }) +
          getTotalCrashes({ isOpeningYear: true, isIntx1: false }),
      },
      designYear: {
        fatalInjury:
          getFiCrashes({ isOpeningYear: false, isIntx1: true }) +
          getFiCrashes({ isOpeningYear: false, isIntx1: false }),
        total:
          getTotalCrashes({ isOpeningYear: false, isIntx1: true }) +
          getTotalCrashes({ isOpeningYear: false, isIntx1: false }),
      },
    };
  }

  const commonProps = {
    intxType: getSafetyIntxType({
      intxType: spfIntersection.type,
      is3LegIntersection,
    }),
    facilityType: safetyFacilityType,
    skewAngle: spfIntersection.safety.skewAngle,
    pennDotDistrict: getDistrictForCounty(county),
    county,
    observedFatalInjuryCrashes,
    observedTotalCrashes,
    yearsOfAnalysis: yearsOfObservedCrashData,
  };

  if (isExpectedCrashCalcPossible) {
    if (
      observedFatalInjuryCrashes === undefined ||
      observedTotalCrashes === undefined
    ) {
      throw new Error(
        "Cannot find expected crash number if there are no observed crashes"
      );
    }
  }

  const commonMajorRoadProps = {
    hasCrosswalk:
      spfIntersection.safety[SPF_FIELD_NAMES.MAJOR_ROAD_HAS_CROSSWALK],
    hasExclusiveLeft:
      spfIntersection.safety[SPF_FIELD_NAMES.MAJOR_HAS_EXCLUSIVE_LEFT],
    hasExclusiveRight:
      spfIntersection.safety[SPF_FIELD_NAMES.MAJOR_HAS_EXCLUSIVE_RIGHT],
    speedLimit: spfIntersection.safety[SPF_FIELD_NAMES.MAJOR_ROAD_SPEED_LIMIT],
  };
  const commonMinorRoadProps = {
    hasCrosswalk:
      spfIntersection.safety[SPF_FIELD_NAMES.MINOR_ROAD_HAS_CROSSWALK],
    hasExclusiveLeft:
      spfIntersection.safety[SPF_FIELD_NAMES.MINOR_HAS_EXCLUSIVE_LEFT],
    hasExclusiveRight:
      spfIntersection.safety[SPF_FIELD_NAMES.MINOR_HAS_EXCLUSIVE_RIGHT],
    speedLimit: spfIntersection.safety[SPF_FIELD_NAMES.MINOR_ROAD_SPEED_LIMIT],
  };
  const getSpfProps = ({ majorRoadAADT, minorRoadAADT }) => ({
    ...commonProps,
    majorRoad: {
      ...commonMajorRoadProps,
      aadt: majorRoadAADT,
    },
    minorRoad: {
      ...commonMinorRoadProps,
      aadt: minorRoadAADT,
    },
  });
  const openingYearSpfProps = getSpfProps({
    majorRoadAADT: majorRoad.openingYearAADT,
    minorRoadAADT: minorRoad.openingYearAADT,
  });
  const designYearSpfProps = getSpfProps({
    majorRoadAADT: majorRoad.designYearAADT,
    minorRoadAADT: minorRoad.designYearAADT,
  });
  return {
    openingYear: {
      fatalInjury: cmf.fatalInjury * fatalInjurySpf(openingYearSpfProps),
      total: cmf.total * totalSpf(openingYearSpfProps),
    },
    designYear: {
      fatalInjury: cmf.fatalInjury * fatalInjurySpf(designYearSpfProps),
      total: cmf.total * totalSpf(designYearSpfProps),
    },
  };
};

export const getCrashTypeCosts = (projectData) => {
  const { costPerCrash } =
    projectData[STEP_STORAGE_KEYS.COST_PARAMETERS_DEFAULTS] || {};
  return {
    fatalInjury:
      costPerCrash?.fatalInjury ??
      window.PENN_DOT_CONSTANTS.COST_PER_CRASH.FATAL_AND_INJURY,
    propertyDamageOnly:
      costPerCrash?.propertyDamageOnly ??
      window.PENN_DOT_CONSTANTS.COST_PER_CRASH.PROPERTY_DAMAGE_ONLY,
  };
};

/**
 * @param {string} analysisType either at grade or ramp terminal
 * @param {string} currentIntx current intersection selected in step 2
 * @param {object} intersection intersection details that we are finding a matching SPF for
 * @param {object[]} intersections
 * @returns object of intersection details for the intersection whose SPF will be used as the basis for a CMF safety calculation
 */
export const getBaseIntxDetailsForCmf = ({
  analysisType,
  currentIntx,
  intersection,
  intersections,
}) => {
  const getIntxDetailsIfSpf = (intxType) => {
    const details = intersections.find(
      ({ safetyAnalysisType, type }) =>
        type === intxType && safetyAnalysisType === SAFETY_INPUT_TYPES.SPF
    );
    if (!details) {
      throw new Error(
        `Could not find spf details for intersection type ${intxType}`
      );
    }

    return details;
  };

  const baseIntersections = getSPFBaseIntersections({
    currentIntersection: currentIntx,
    analysisType,
    intxType: intersection.type,
  });

  if (
    intersection.safetyAnalysisType === SAFETY_INPUT_TYPES.CMF_USER_ENTERED ||
    baseIntersections?.length > 1
  ) {
    // if there's multiple SPF options, go with the one the user selected in the dropdown
    return getIntxDetailsIfSpf(intersection.safety.cmf.spfIntxType);
  }

  // if there's no mandatory baseIntersections for the SPF, just use the current intersection
  if (!baseIntersections?.length) {
    return getIntxDetailsIfSpf(currentIntx);
  }

  if (baseIntersections.length !== 1) {
    throw new Error(
      `intersection type ${intersection.type} was expected to have only one base intersection option`
    );
  }
  // if there's only one base intersection SPF option, use it
  return getIntxDetailsIfSpf(baseIntersections[0]);
};

/**
 * @param {object} intersection details object with intersection type, uniqueName, etc.
 * @param {object} projectData
 * @returns object with keys openingYear, designYear, and spfIntersection
 */
export const getCrashesPerYear = ({ intersection, projectData }) => {
  const { currentIntersection: currentIntx } =
    projectData[STEP_STORAGE_KEYS.GLOBAL_INPUT_DATA];
  const safetyAnalysisType = getSafetyAnalysisType({
    currentIntx,
    intxType: intersection.type,
  });
  const intersections = projectData[STEP_STORAGE_KEYS.INTERSECTIONS];
  const analysisType = getAnalysisType(projectData);

  if (
    safetyAnalysisType === SAFETY_INPUT_TYPES.CMF ||
    safetyAnalysisType === SAFETY_INPUT_TYPES.CMF_USER_ENTERED
  ) {
    // get SPF to apply CMF to
    const spfIntersection = getBaseIntxDetailsForCmf({
      analysisType,
      currentIntx,
      intersection,
      intersections,
    });

    // calculate SPF and apply CMF
    return {
      ...getCrashesPerYearForSpfIntersection({
        cmf: intersection.safety.cmf, // each element in the intersections array should have this safety.cmf object all ready with the proper CMFs. See function getProjectDataWithAddedSafetyInfo() for this logic
        projectData,
        spfIntersection,
      }),
      spfIntersection,
    };
  }

  if (safetyAnalysisType === SAFETY_INPUT_TYPES.SPF) {
    // get crashes using SPF and do not pass a CMF value
    return {
      ...getCrashesPerYearForSpfIntersection({
        projectData,
        spfIntersection: intersection,
      }),
      spfIntersection: intersection,
    };
  }

  throw new Error(
    `Could not calculate safety costs for safety analysis type ${safetyAnalysisType}`
  );
};

/**
 * @param {object} [formData] live data from an active form
 * @param {object} projectData all saved data from the project
 * @returns a project data object with the same shape, except that the intersections have safety info added such as:
 * safetyAnalysisType (CMF, USER_ENTERED_CMF, or SPF)
 * live form data on which CMF/SPF should be used and with which parameters
 */
export const getProjectDataWithAddedSafetyInfo = ({
  formData,
  projectData,
}) => {
  const currentIntx =
    projectData[STEP_STORAGE_KEYS.GLOBAL_INPUT_DATA].currentIntersection;
  const intersections = projectData[STEP_STORAGE_KEYS.INTERSECTIONS];
  const analysisType = getAnalysisType(projectData);
  const intersectionsWithSafetyAnalysisTypes = intersections.map((i) => ({
    ...i,
    safetyAnalysisType: getSafetyAnalysisType({
      currentIntx,
      intxType: i.type,
    }),
  }));
  const intersectionsWithUpdatedSafety =
    intersectionsWithSafetyAnalysisTypes.map((i) => {
      // if there is live form data passed in (formData) use that
      // otherwise use the saved form data from the last save
      const combinedFormData = formData?.[i.id] ?? i.safety ?? {};

      if (
        [SAFETY_INPUT_TYPES.SPF, SAFETY_INPUT_TYPES.CMF_USER_ENTERED].includes(
          i.safetyAnalysisType
        )
      ) {
        return { ...i, safety: combinedFormData };
      }

      const baseIntx =
        combinedFormData.cmf?.spfIntxType ??
        getBaseIntxDetailsForCmf({
          analysisType,
          currentIntx,
          intersection: i,
          intersections: intersectionsWithSafetyAnalysisTypes,
        })?.type;

      // if we are using CMFs that are not user entered, calculate what the cmf should be
      const [fatalInjuryCmf, totalCmf] = getCmfsForIntxType({
        areaType:
          combinedFormData.cmf?.areaType ?? getGlobalAreaType(projectData),
        baseIntx,
        cmfOverrides: getCmfOverridesForIntersection({
          intxType: i.type,
          projectData,
        }),
        currentIntx,
        intxType: i.type,
        speedLimit: combinedFormData.speedLimit,
      });

      return {
        ...i,
        safety: {
          ...combinedFormData,
          cmf: {
            ...combinedFormData.cmf,
            fatalInjury: fatalInjuryCmf.overrideValue ?? fatalInjuryCmf.value,
            total: totalCmf.overrideValue ?? totalCmf.value,
          },
        },
      };
    });
  return {
    ...projectData,
    [STEP_STORAGE_KEYS.INTERSECTIONS]: intersectionsWithUpdatedSafety,
  };
};
